import { ID_PART_PATTERN as ID_PART_PATTERN$1, MAX_FIXTURE_ID_LENGTH, VERSION_SLUG_PATTERN, instanceKey } from "./instance-identity.js";
import { APP_OFFER_APPROVALS, APP_POOL_TIER_CAPS_MAX, DIGEST_PINNED_IMAGE_REF_PATTERN, FIXTURE_ISOLATION_LEVELS, PER_APP_IDENTITY_PROVIDERS } from "./desired-state.js";
import { LOWERCASED_UUID_PATTERN } from "./deployment-config.js";
import { GSA_SERVICE_ACCOUNT_EMAIL_PATTERN } from "./gsa-identity.js";
import { appBlobBindingsSchema, blobBindingsFileSchema } from "./blob-bindings.js";
import { appClaimCheckBindingsSchema } from "./claim-check-bindings.js";
import { BLOB_RELOCATION_REFUSAL_PATTERN, BLOB_RELOCATION_STATE_VALUES, RELOCATION_GATES, isOpenBlobRelocationState } from "./relocation.js";
import { PLATFORM_BUCKETS_MAX_ORGS } from "./platform-buckets.js";
import { rightsCertificateOnWireSchema } from "./rights-certificate-wire.js";
import { APP_POOL_LABEL_VALUE_PATTERN, APP_POOL_MAX_NODES_MAX, APP_POOL_MAX_NODES_MIN, AUDIT_LEVELS, BLOB_BUCKETS_PER_APP_MAX, CHANNEL_SHARES_PER_ORG_MAX, CHANNEL_SHARE_PATTERN, DEFAULT_ROLES_MAX, LIBRARY_SLUG_MAX_LENGTH, LIBRARY_SLUG_PATTERN, LIBRARY_USE_CONFIG_MAX_BYTES, LIBRARY_USE_CONFIG_MAX_DEPTH, ORG_ROLE_SLUG_PATTERN, PLATFORM_PERMISSION_KEY_PATTERN, PRIVATE_CHANNELS_PER_NAMESPACE_MAX, PRIVATE_CHANNEL_SLUG_PATTERN, RESOURCE_SLUG_PATTERN, WITHHOLD_CODES, allowedLocationsSchema, appPoolNameSchema, appPoolOrgIdSchema, appPoolTierKeySchema, blobBucketNameSchema, channelDisplayNameSchema, computeClassSchema, dataLocationSchema, isFirstPartyApp, isLibraryConfigWithinBounds, matchesChannelSharePattern, namedFixtureBlobStorageSchema, privateChannelKeyFor, sizeSchema } from "@zion/sdk-contracts";
import { z } from "zod";
//#region src/desired-state.schemas.ts
/**
* Zod schemas for the multi-version desired-state shape (plan 25 + 24,
* reshaped by the 2026-05-14 cp-dp-envelope-opaque-manifests refactor).
*
* Used by:
*   - CP release-engine — validates the operator-API response body it
*     emits to the DP operator on each desired-state poll.
*   - DP operator — validates the response before invoking reconcile loops.
*
* Strict — extra fields on the typed sidecar are rejected. The `appManifest`
* and `fixtureManifest` blobs are intentionally `z.record(z.unknown())` —
* their shape is owned by the SDK JSON schemas in `@zion/sdk-contracts`,
* validated separately via Ajv on the consumer side.
*/
const ID_PART = z.string().regex(/^[a-z]([a-z0-9-]*[a-z0-9])?$/);
const VERSION_SLUG = z.string().regex(/^v[a-z0-9]([a-z0-9-]*[a-z0-9])?$/);
/**
* Registry-portable, digest-pinned image reference:
* `<repository>@sha256:<64-hex>` (or
* `<namespace>/<repository>@sha256:<64-hex>`).
*
* **No registry prefix.** The colon (which appears in registries with
* ports like `zion-registry:5000` or `192.168.139.84:5000`) is
* deliberately NOT in the leading char class — that rejects the full
* `<reg>/<repo>@sha256:<hex>` form at the CP/DP boundary. Each
* consumer constructs the full pull URL from its own
* `IMAGE_REGISTRY` env (`${IMAGE_REGISTRY}/${imageRef}`).
*
* ⛔ THE PATTERN IS IMPORTED, NEVER RESTATED (app-pools WS-2). It was the literal
* `/^[A-Za-z0-9._-]+(\/[A-Za-z0-9._-]+)*@sha256:[a-f0-9]{64}$/` here, spelled a second time in
* `release-engine`'s `DIGEST_PIN_RE` and about to be spelled a third for the app-pool wire.
* {@link DIGEST_PINNED_IMAGE_REF_PATTERN} is now the one declaration in this package and this
* field's behaviour is byte-identical. ⚠️ No `.max()` here, deliberately: adding one would change
* the shipped fixture wire, and this edit is a de-duplication, not a tightening. The app-pool
* fields carry their own bound through `digestPinnedImageRefSchema`.
*/
const IMAGE_REF = z.string().regex(DIGEST_PINNED_IMAGE_REF_PATTERN);
/** Plan 25 — version routing record. */
const VersionRouteSchema = z.object({
	versionSlug: VERSION_SLUG,
	channels: z.array(z.string().min(1)).readonly(),
	isDefault: z.boolean(),
	isOwner: z.boolean()
}).strict();
/**
* Spec for one platform-generated random secret declared by a fixture
* version (G34, 2026-05-09). Both the SDK contract and the fixture.json
* schema validate the same shape; the operator validates again at
* reconcile time before generating any bytes.
*
* - `byteLength` MUST be in [16, 64]. 32 = HMAC-SHA256 / JWT_SECRET-shaped.
* - `encoding` allows `base64url` (url-safe, pad-free) or `hex`. `base64`
*   with `=` padding is intentionally rejected — pad chars confuse
*   downstream URL consumers.
* - The map KEY (validated by the parent record schema) is the env var
*   name and Secret data key; it must match `^[A-Z][A-Z0-9_]*$` to
*   project as a Node-convention env var.
*/
const FixtureDesiredPlatformSecretSpecSchema = z.object({
	byteLength: z.number().int().min(16).max(64),
	encoding: z.enum(["base64url", "hex"]),
	description: z.string().optional()
}).strict();
/**
* Env-var-shaped key pattern: UPPER_SNAKE_CASE so `envFrom` projects the
* secret data into Node-convention env vars. G34 lock-down — drift back
* to lowercase would silently produce `process.env.jwtsecret` and crash
* every consumer that reads `process.env.JWT_SECRET`.
*/
const PLATFORM_SECRET_KEY = z.string().regex(/^[A-Z][A-Z0-9_]*$/);
/**
* Per-fixture infrastructure resources (G14-B + G34). Mirrors the
* `fixture.json` `resources` block. Operators read this to decide
* which envFrom blocks (DB, Redis, blob storage, platformSecrets) to
* inject on this fixture's pods.
*
* Names here are CP/provisioning-svc allocations and the operator must NOT
* synthesize them from the manifest blob (plan §10.2 / §12.1 caveat).
*
* ⛔ EXCEPT `database.ownSchema`, which is not an allocation and never was. The
* operator resolves that name itself. See the TS twin's docblock on
* `FixtureDesiredDatabaseResource.ownSchema`; this comment is narrowed rather
* than deleted because the rule still holds for every other name here.
*/
const FixtureDesiredResourcesSchema = z.object({
	database: z.object({
		ownSchema: z.string().min(1).optional(),
		readSchemas: z.array(z.string().min(1)).readonly().optional(),
		search: z.boolean().optional()
	}).strict().optional(),
	redis: z.boolean().optional(),
	coreApiAccess: z.boolean().optional(),
	multiOrgAgentHost: z.boolean().optional(),
	machineEnrollment: z.boolean().optional(),
	orgCrypto: z.boolean().optional(),
	audit: z.boolean().optional(),
	llm: z.boolean().optional(),
	eventBus: z.boolean().optional(),
	coordinationKv: z.array(z.string().min(1)).readonly().optional(),
	consumesStreams: z.array(z.string().regex(/^[a-z0-9-]{1,40}$/, "consumesStreams items must be valid principal ids (^[a-z0-9-]{1,40}$)")).readonly().optional(),
	consumesAllStreams: z.boolean().optional(),
	blobStorage: z.union([z.object({
		bucket: z.string().min(1),
		maxSizeGb: z.number().positive().optional()
	}).strict(), namedFixtureBlobStorageSchema]).optional(),
	platformSecrets: z.record(PLATFORM_SECRET_KEY, FixtureDesiredPlatformSecretSpecSchema).optional()
}).strict();
/** Persisted-config sidecar entry. */
const FixtureConfigValueSchema = z.object({
	key: z.string().min(1),
	value: z.string()
}).strict();
/** One withheld app or fixture on the envelope (#2617); see `WithheldEntry` in `desired-state.ts`.
*  The CP parses each entry with this schema when it assembles the list (R-032). */
const WithheldEntrySchema = z.object({
	appSlug: z.string().min(1).nullable(),
	fixtureId: z.string().min(1).nullable(),
	code: z.enum(WITHHOLD_CODES),
	detail: z.string().min(1).max(2048)
}).strict();
/** The list's bound: one entry per app slug plus one per data-plane code. `apps` declares no cap,
*  so this is the list's own; the CP refuses to assemble past it (R-032). */
const WITHHELD_ENTRIES_MAX = 512;
const WithheldListSchema = z.array(WithheldEntrySchema).max(512);
/** Reference to an external Secret Manager entry projected on the pod. */
const FixtureSecretRefSchema = z.object({
	key: z.string().min(1),
	secretName: z.string().min(1),
	secretKey: z.string().min(1)
}).strict();
/** Cross-fixture endpoint dependency. */
const FixtureDependencyEntrySchema = z.object({
	fixtureId: ID_PART,
	versionRange: z.string().min(1)
}).strict();
/**
* Schema for `FixtureVersionedDesiredState` post cp-dp-envelope refactor.
*
* The `fixtureManifest` field is the opaque blob — `z.record(z.unknown())`
* intentionally accepts any shape because the manifest's structure is
* validated by the SDK Ajv schema on the consumer side. Validating it
* twice (once here with zod, once again with Ajv) would be a maintenance
* trap — drift between the zod schema and the JSON schema would surface
* as inconsistent error messages.
*/
/** Ring vocabulary (app release pipeline plan §A.3) — mirrors RINGS. */
const RING_SCHEMA = z.enum([
	"stable",
	"beta",
	"alpha",
	"edge"
]);
const FixtureMcpCapabilityOnWireSchema = z.object({
	id: z.string().min(1).regex(/^[a-z][a-z0-9-]*$/),
	path: z.string().min(1),
	label: z.string().min(1)
}).strict();
const FixtureCapabilitiesOnWireSchema = z.object({ mcp: z.array(FixtureMcpCapabilityOnWireSchema).readonly().optional() }).strict();
const FixtureVersionedDesiredStateSchema = z.object({
	version: z.string().min(1),
	versionSlug: VERSION_SLUG,
	channels: z.array(z.string().min(1)).readonly(),
	isOwner: z.boolean(),
	ringName: RING_SCHEMA.optional(),
	imageRef: IMAGE_REF,
	imageTag: z.string().min(1),
	bundleUrl: z.string().min(1).optional(),
	cssUrl: z.string().nullable().optional(),
	fixtureManifest: z.record(z.string(), z.unknown()),
	resources: FixtureDesiredResourcesSchema.optional(),
	configValues: z.array(FixtureConfigValueSchema).readonly().optional(),
	secretRefs: z.array(FixtureSecretRefSchema).readonly().optional(),
	minPlatformVersion: z.string().min(1),
	dependencies: z.array(FixtureDependencyEntrySchema).readonly().optional(),
	expandMigrations: z.array(z.string().min(1)).readonly().optional(),
	/**
	* C-6 / D-7 / C-22: this fixture VERSION's app-declared capabilities. See
	* `FixtureVersionedDesiredState.capabilities`.
	*
	* WIDENING A PUBLISHED `.strict()` SCHEMA, AND THE ORDERING REASON HERE IS NOT THE ONE
	* `AppFixtureSet`'s two removed members (`isolation`, `workTargets`) recorded. For those the DP
	* shipped first because an operator WITHOUT the field ignores it, which is a soft ordering. This
	* member's ordering is HARD and it comes from somewhere else entirely: the value originates in
	* `fixture.json`, whose JSON Schema is `additionalProperties: false` and which the operator
	* RE-VALIDATES with Ajv at reconcile, throwing `ManifestValidationError`. An operator that has
	* not learned the manifest member rejects the whole manifest, and the app leaves that data
	* plane. Do not read the softer reason as covering this one. See WS-4 section 3.
	*
	* This schema is no runtime control for the operator program. Since #1009 its
	* `CpClient.getDesiredState()` parses each 200 body with `parseDesiredStateEnvelope`. That
	* function checks the eleven paths of `DesiredStateEnvelopeSchema` and keeps every other key.
	* It runs no member schema, so `.strict()` binds only consumers inside this package and
	* CP-side tests.
	*
	* `.optional()` and NOT `.nullable()`: there is no `null` on this wire. Absent means this
	* version declares none, and absence is byte-identical to today's envelope.
	*/
	capabilities: FixtureCapabilitiesOnWireSchema.optional(),
	/**
	* 2026-10-03-kernel-apps WS-2a (R-023): this active version's rights certificate. See
	* `FixtureVersionedDesiredState.rightsCertificate`. `.optional()` and never `.nullable()`:
	* absence is byte-identical to the envelope before this member. Appended after
	* `capabilities`, so the emitted key order of every older member holds.
	*/
	rightsCertificate: rightsCertificateOnWireSchema.optional()
}).strict();
/**
* App declared-permission entry on the envelope wire (§A.3 / F3).
*
* `defaultRoles` (zion-cli-integrate-verbs D7): the org role slugs the DP onboard step grants
* this key to, each bound by `ORG_ROLE_SLUG_PATTERN` from `@zion/sdk-contracts`, the same
* pattern the manifest schema carries. `.optional()` and not `.nullable()`: absent means the
* manifest did not say, and absence is byte-identical to the envelope before the field existed,
* so a CP that emits it to a DP whose operator predates it changes nothing there.
*/
const AppDeclaredPermissionOnWireSchema = z.object({
	key: z.string().min(1),
	label: z.string().min(1),
	category: z.string().min(1).optional(),
	isDefault: z.boolean(),
	defaultRoles: z.array(z.string().regex(ORG_ROLE_SLUG_PATTERN)).max(DEFAULT_ROLES_MAX).readonly().optional(),
	description: z.string().optional(),
	/**
	* #4416. The app version that declares this key. The CP sends the declarations of every app
	* version on the data plane. Absent from an older CP; the operator then writes the key under
	* the app-level `appVersion`.
	*/
	appVersion: z.string().min(1).optional()
}).strict();
/**
* 2026-09-07-org-members-read-for-apps (R-021, R-029, R-034) — the wire form of the platform
* permissions a version requests, one declaration composed into `AppDesiredStateSchema` and imported
* by release-engine's projection, so nothing re-spells the bound. Bound by
* `PLATFORM_PERMISSION_KEY_PATTERN` and by nothing narrower. `.strict()` and this bound are
* producer-side controls: the operator parses the envelope with the loose `DesiredStateEnvelopeSchema`
* only, which does not check this member (see the warnings on `AppDesiredStateSchema` below), so
* nothing here ever refused an envelope on a data plane. An enum would need every producer and every future consumer edited when the allowlist
* grows; the pattern does not.
*/
const AppPlatformPermissionRequestsOnWireSchema = z.array(z.string().regex(PLATFORM_PERMISSION_KEY_PATTERN)).readonly();
/** App declared-audit-event entry on the envelope wire (contracts §12.1). */
const AppDeclaredAuditEventOnWireSchema = z.object({
	verb: z.string().min(1).max(128).regex(/^[a-z][a-z0-9._-]*$/),
	label: z.string().min(1).max(128).regex(/^[^\p{Cc}\p{Cf}]+$/u),
	/**
	* ⛔ C-106 — DRIVEN OFF `AUDIT_LEVELS`, never re-spelled. A hand-copied member list
	* admits a fifth level the validator then returns typed as one of four, and the DDL
	* `level_check` raises 23514 inside the registration transaction: a 500 where the
	* design intends a 400.
	*/
	level: z.enum(AUDIT_LEVELS),
	description: z.string().max(512).regex(/^[^\p{Cc}\p{Cf}]*$/u).optional(),
	/**
	* ⛔ C-102 — THE KEY NAME CARRIES `label`'s Cc/Cf RULE, because it reaches the same
	* surface. A declared key propagates into an emitted row's `details` and renders as
	* audit detail, so a bidi or zero-width key name is the same display-integrity defect
	* a bidi label is. The three strings arriving from one manifest at one door were held
	* to two different rules; this is the half that was missing.
	*
	* ⛔ C-103 — THE BOUNDS MATCH AJV EXACTLY, not "something reasonable". Ajv is upstream
	* and caps at `propertyNames.maxLength: 64` and `maxProperties: 32`; this schema
	* accepted a 5,000-character key and 10,000 keys. That is not reachable today, and it
	* is still the defect: this schema's own comments call it the standalone guard for the
	* one column that reaches a DP `jsonb` write inside a single SERIALIZABLE transaction,
	* and a guard documented as standalone must be able to stand alone. Two schemas that
	* disagree while one is unreachable are drift waiting for the day it becomes reachable.
	*
	* ⚠️ The key count is a `.refine` rather than a bound on the record, because zod has no
	* `.max()` on a record. It is therefore NOT emitted as `maxProperties` by
	* `z.toJSONSchema`, which is why F-9 pins that one bound behaviourally and the other
	* two byte for byte — see the note there before "simplifying" either half.
	*/
	details: z.record(z.string().max(64).regex(/^[^\p{Cc}\p{Cf}]+$/u), z.enum([
		"string",
		"number",
		"boolean"
	])).refine((d) => Object.keys(d).length <= 32, { message: "details may declare at most 32 keys" }).optional(),
	ocsf: z.object({
		classUid: z.int().min(0).max(2147483647),
		activityId: z.int().min(0).max(2147483647)
	}).strict().optional(),
	/**
	* #4416. The app version that declares this entry. The CP sends the declarations of every
	* app version on the data plane, so one verb can arrive once per version. Absent from an
	* older CP; the operator then writes the entry under the app-level `appVersion`.
	*/
	appVersion: z.string().min(1).optional()
}).strict();
/**
* #4416: one fixture pin of an app version. `versionSlug` is the value the CP computes with
* `versionSlug(version)` and the operator puts on the pod label `zion.witness.ai/version`, so the
* data plane never computes a slug itself.
*/
const AppVersionFixturePinSchema = z.object({
	fixtureId: z.string().max(MAX_FIXTURE_ID_LENGTH).regex(ID_PART_PATTERN$1),
	version: z.string().min(1),
	versionSlug: z.string().max(16).regex(VERSION_SLUG_PATTERN)
}).strict();
/** Customer Channel descriptor echoed in StatusReport (§A.3). */
const CustomerChannelDescriptorSchema = z.object({
	id: z.string().min(1),
	orgId: z.string().min(1),
	name: z.string().min(1),
	ring: RING_SCHEMA,
	isDefault: z.boolean()
}).strict();
/** Per-org minimum-ring audit metadata that rides with `minimumRingByOrg`. */
const MinimumRingMetadataSchema = z.object({
	setByEmail: z.string().min(1),
	setAt: z.string().min(1),
	reason: z.string().optional()
}).strict();
/**
* WS-7 — the zod twin of `FixtureIsolationLevel`.
*
* ⛔ THE NAME IS LOAD-BEARING, not stylistic. `__tests__/zod-coverage.spec.ts` pairs an
* exported type `Foo` with an identifier matching /^[A-Z][A-Za-z0-9_]*Schema$/ found in a
* `*.schemas.ts` file. Exporting the TYPE without this exactly-named schema adds to the
* ratchet's denominator and not its numerator.
*/
const FixtureIsolationLevelSchema = z.enum(FIXTURE_ISOLATION_LEVELS);
/**
* A full GCP service-account email on the CP-to-DP wire: `<account-id>@<project>.iam.gserviceaccount.com`.
*
* The DP operator stamps this value verbatim on a ServiceAccount's
* `iam.gke.io/gcp-service-account` annotation, so the CP must check it before it sends it. Both
* `AppDesiredState.gcpServiceAccount` and `AppFixtureSet.pinnedGcpServiceAccount` compose this one
* schema. The length bound aborts the chain, so the pattern never runs on an over-long input (the
* repo's ReDoS rule; zod 4 runs `.regex()` after a failed `.max()` unless `abort` is set). The
* message names the shape, not the pattern.
*/
const GcpServiceAccountEmailOnWireSchema = z.string().max(85, { abort: true }).regex(GSA_SERVICE_ACCOUNT_EMAIL_PATTERN, "must be a GCP service-account email of the form <account-id>@<project>.iam.gserviceaccount.com, where <account-id> is 6-30 chars of [a-z0-9-] starting with a letter and ending with a letter or digit");
/** Plan 25 — set of versions for one fixture on the DP. */
const AppFixtureSetSchema = z.object({
	activeVersions: z.array(FixtureVersionedDesiredStateSchema).readonly(),
	defaultVersionsByOrg: z.record(z.string().min(1), VERSION_SLUG),
	ownerVersion: z.string().min(1),
	/**
	* The CP-pinned Google service account. See `AppFixtureSet.pinnedGcpServiceAccount`.
	*
	* The operator's `cp-client` parses the envelope with the loose `DesiredStateEnvelopeSchema`,
	* which does not check this member, and the CP parses no whole envelope before it sends it. So release-engine safe-parses each pin with {@link GcpServiceAccountEmailOnWireSchema}
	* when it builds the set (plan 2026-10-09-zion-build-signer-identity, WS-2). `.optional()` and
	* not `.nullable()`: absence is byte-identical to today's envelope.
	*/
	pinnedGcpServiceAccount: GcpServiceAccountEmailOnWireSchema.optional()
}).strict().refine((s) => s.activeVersions.length > 0, { message: "AppFixtureSet.activeVersions must contain at least one entry" });
/**
* Schema for `AppDesiredState` post cp-dp-envelope refactor.
*
* `appManifest` mirrors `fixtureManifest` — opaque blob, shape owned by
* the SDK Ajv schema in `@zion/sdk-contracts`.
*/
/** D22 — the envelope-shape zod twin of `AppComputeOverride` (`desired-state.ts`). `.strict()`, and
*  NON-NULLABLE: a cleared half is an ABSENT KEY here, never `null` (the operator's "no override for
*  this half" branch keys on absence). ⛔ Distinct from `appComputeOverridePatchSchema`
*  (`@zion/sdk-contracts`'s `dp-compute-view.ts`), which IS nullable because null is how the WIRE
*  says "clear this half". Conflating them puts a `null` on the DP wire the operator would have to
*  special-case. WS-20 `safeParse`s every persisted row through this before it reaches the envelope,
*  so a corrupt row is dropped with a log line rather than shipped to every operator. */
const appComputeOverrideSchema = z.object({
	class: computeClassSchema.optional(),
	size: sizeSchema.optional()
}).strict();
/**
* The grammar of `AppLibraryDesiredState.consumerSlug`: the `appId` grammar of
* `app-manifest.schema.json`. A consumer is an app or an instance with a published version, so its
* slug is the `appId` of that version and has at most 30 characters. An app slug may hold `--`; a
* library slug may not (`LIBRARY_SLUG_PATTERN`).
*/
const LIBRARY_CONSUMER_SLUG = z.string().max(LIBRARY_SLUG_MAX_LENGTH).regex(/^[a-z][a-z0-9-]*[a-z0-9]$/);
/**
* The zod form of `AppLibraryDesiredState` (2026-10-12-shared-libraries). Strict. `librarySlug`
* follows `LIBRARY_SLUG_PATTERN` of `@zion/sdk-contracts` (no `--`, so `libraryInstanceSlug`
* accepts every slug the block accepts). `consumerSlug` follows the `appId` grammar of
* `app-manifest.schema.json` with its 30-character cap: a consumer has a published version, so its
* slug is that version's `appId`, and the 63-character cap of `createAppSchema` never reaches here.
* `consumerConfig` is a JSON object that nests at most 32 containers (`LIBRARY_USE_CONFIG_MAX_DEPTH`)
* and holds at most 65,536 bytes of `JSON.stringify` text (`LIBRARY_USE_CONFIG_MAX_BYTES`);
* `isLibraryConfigWithinBounds` checks the depth before it counts bytes, so the parse never throws
* on a deep value. No `.transform()`.
*/
const AppLibraryDesiredStateSchema = z.object({
	librarySlug: z.string().max(LIBRARY_SLUG_MAX_LENGTH).regex(LIBRARY_SLUG_PATTERN),
	consumerSlug: LIBRARY_CONSUMER_SLUG,
	consumerConfig: z.record(z.string(), z.unknown()).refine(isLibraryConfigWithinBounds, { message: `consumerConfig exceeds ${LIBRARY_USE_CONFIG_MAX_DEPTH} levels or ${LIBRARY_USE_CONFIG_MAX_BYTES} bytes` })
}).strict();
/** The delimiter of `instanceKey`: `<app>--<fixture>`. */
const PER_APP_ACCOUNT_DELIMITER = "--";
/**
* True when `name` is `<app>--<fixture>` with each part in `ID_PART_PATTERN`, the app part at most
* `MAX_APP_ID_LENGTH` characters and the fixture part at most `MAX_FIXTURE_ID_LENGTH` (rule 2 of
* `PerAppIdentity`). `ID_PART_PATTERN` refuses `--` inside a part, so a valid name splits into
* exactly two parts. Pure; throws nothing.
*/
function isPerAppAccountName(name) {
	const parts = name.split(PER_APP_ACCOUNT_DELIMITER);
	if (parts.length !== 2) return false;
	const [app, fixture] = parts;
	return ID_PART_PATTERN$1.test(app) && app.length <= 30 && ID_PART_PATTERN$1.test(fixture) && fixture.length <= MAX_FIXTURE_ID_LENGTH;
}
/** True when each name is greater than the name before it in code-unit order (rule 3). Pure. */
function isStrictlyAscending(names) {
	return names.every((name, index) => index === 0 || names[index - 1] < name);
}
/**
* 2026-10-26-aws-followups D-8 (#4629): the runtime half of {@link PerAppIdentity}, the value of
* `AppDesiredState.perAppIdentity`. It checks rules 1, 2, 3 and 5 of `PerAppIdentity`.
* `AppDesiredStateSchema` checks rule 4: the list is exactly one name per key of the entry's
* `fixtures`, each with the entry's `appId` as its app part.
*
* - A conforming value parses to itself. No `.transform()` and no `.default()`.
* - An empty `serviceAccounts` fails with the issue path `serviceAccounts`.
* - A name outside rule 2 fails with the issue path `serviceAccounts.<index>`.
* - A list out of strict ascending order, or with a duplicate, fails with the issue path
*   `serviceAccounts`.
* - A `provider` outside `PER_APP_IDENTITY_PROVIDERS` fails with the issue path `provider`.
* - An unknown key fails with `unrecognized_keys`.
*
* The CP producer (WS-4b) parses the member alone with this schema before it assigns the member,
* and omits the member on a failure. The operator program (WS-4c) parses the member alone too,
* because its envelope parse does not check the member. Pure; side-effect free.
*/
const PerAppIdentitySchema = z.object({
	provider: z.enum(PER_APP_IDENTITY_PROVIDERS),
	serviceAccounts: z.array(z.string().refine(isPerAppAccountName, { message: `a service account must be <app>--<fixture>: each part in ID_PART_PATTERN, the app part at most 30 characters, the fixture part at most ${MAX_FIXTURE_ID_LENGTH}` })).min(1, { message: "serviceAccounts must hold at least one name; omit the member instead" }).refine(isStrictlyAscending, { message: "serviceAccounts must be in strictly ascending code-unit order, with no duplicate" }).readonly()
}).strict();
const AppDesiredStateSchema = z.object({
	appId: ID_PART,
	appVersion: z.string().min(1),
	bundleUrl: z.string().min(1).optional(),
	cssUrl: z.string().nullable().optional(),
	appManifest: z.record(z.string(), z.unknown()),
	fixtures: z.record(ID_PART, AppFixtureSetSchema),
	/** §A.3 — stable UUID identity for FK target on DP apps_catalog. */
	appUuid: z.string().uuid().optional(),
	/**
	* §A.3 — declared permissions threaded from app.json. Three-state since #2878 (absent, `[]`,
	* `[...]`); the table on `AppDesiredState.declaredPermissions` gives each state's meaning
	* and the operator's response.
	*/
	declaredPermissions: z.array(AppDeclaredPermissionOnWireSchema).readonly().optional(),
	/**
	* 2026-09-07-org-members-read-for-apps (R-021) — the platform permissions this version requests,
	* threaded from `app.json` `platformPermissions[]`. CP admits the publish against
	* `REQUESTABLE_PLATFORM_PERMISSION_KEYS` and stamps the member whenever it resolved `appUuid`, an
	* empty array included, so a version that drops a request retracts it; the operator writes
	* `release.app_platform_permission_requests` by a delete scoped to the apps whose entry carries the
	* member, then an insert, and skips the write when the member is absent (R-031). The shape is
	* {@link AppPlatformPermissionRequestsOnWireSchema}, the one declaration.
	*/
	platformPermissionRequests: AppPlatformPermissionRequestsOnWireSchema.optional(),
	/** contracts §12.1 — declared audit verbs threaded from app.json. */
	declaredAuditEvents: z.array(AppDeclaredAuditEventOnWireSchema).readonly().optional(),
	/**
	* WS-2 (D-2 / C-7 / C-9) — the licensed-organization SLUG set. See
	* `AppDesiredState.entitledOrgSlugs` for the states, why they are not interchangeable, and
	* why this is keyed by slug rather than by UUID.
	*
	* ⛔ `.optional()` AND NEVER A DEFAULT. A `.default([])` would erase the difference between
	* "CP does not know about licensing" and "CP says nobody", which is the whole contract. There
	* is no `null` on this wire either: absence is the carrier of the first state.
	*
	* ⛔ NOT `z.uuid()`. The neighbouring `appUuid` key is a UUID and this one is deliberately
	* not — C-9 measured CP and the DP holding different UUIDs for the same tenant. A reviewer
	* who "harmonises" this to match its neighbour reintroduces a fleet-wide revoke.
	*
	* This member widens a published `.strict()` schema. Do not write here that an older operator
	* program rejects the envelope. Before #1009 its `cp-client` ran no zod (measured 2026-08-16:
	* `grep -c zod cp-client/*.ts` returned 0 across all four files). Since #1009
	* `CpClient.getDesiredState()` runs `parseDesiredStateEnvelope`, which keeps every unknown key
	* and runs no member schema. So `.strict()` is a CP-side producer control. The real ordering
	* reason is the ordinary one: an operator program without the field ignores it, so the CP may
	* emit before the DP learns it.
	*/
	entitledOrgSlugs: z.array(z.string().min(1)).readonly().optional(),
	/** §A.3 / F13 — CP-marked degraded flag. */
	degraded: z.boolean().optional(),
	/**
	* WS-5 — the per-app GSA email.
	*
	* This member widens a published schema. Do not write here that an old operator program
	* rejects the envelope. Before #1009 its `cp-client` ran no zod (measured 2026-08-16). Since
	* #1009 `CpClient.getDesiredState()` runs `parseDesiredStateEnvelope`, which keeps every
	* unknown key and runs no member schema. So `.strict()` is no runtime control for a consumer
	* outside this package. The real reason the DP ships first: an operator program without this
	* field ignores it. The app then stays on `{xr}-loki` with no signal, while the CP logs a move.
	* See CG-2.
	*/
	gcpServiceAccount: GcpServiceAccountEmailOnWireSchema.optional(),
	/**
	* 2026-10-03-kernel-apps WS-2a (R-023): the rights certificate of `appVersion`. See
	* `AppDesiredState.rightsCertificate` for the two states.
	*
	* The CP producer (WS-8b) must parse the member value alone with `rightsCertificateOnWireSchema`
	* before it assigns the member. It must not parse the whole entry with `AppDesiredStateSchema`:
	* this schema is `.strict()` and has no `pools`, `bindings`, `autoscalers` or
	* `refusedNodeGroups` member, which the interface `AppDesiredState` declares. So a whole-entry
	* parse refuses the entry of every pooled app with `unrecognized_keys`. No CP code parses an
	* entry with this schema today. The spec `desired-state-rights-certificate.spec.ts` (D-08) pins
	* that refusal, so the arm goes red when a change adds the four members. The operator program
	* parses each envelope with `DesiredStateEnvelopeSchema`. That schema checks eleven paths: nine
	* are containers, and two (`appManifest`, `fixtureManifest`) are presence checks that accept any
	* value except `undefined` and `null`. On this entry it checks `appManifest` and `fixtures`
	* only. It runs no schema on this member, so the operator program applies the same member
	* bound itself (WS-22).
	*/
	rightsCertificate: rightsCertificateOnWireSchema.optional(),
	/**
	* 2026-10-06-tenant-app-adoption C-5 (D-16): the manifest of every active version, keyed by the
	* app version. See `AppDesiredState.appManifestsByVersion` for the two states and the rules.
	* Each key is a non-empty string, as `appVersion` is. Each value is the opaque blob that
	* `appManifest` carries. An empty map is refused, so absence is the one carrier of "no map".
	* The key order is a producer rule and this schema does not check it. No `.transform()`.
	*/
	appManifestsByVersion: z.record(z.string().min(1), z.record(z.string(), z.unknown())).refine((byVersion) => Object.keys(byVersion).length > 0, { message: "appManifestsByVersion must hold at least one version; omit the member instead" }).optional(),
	/**
	* 2026-10-12-shared-libraries: the `library` block of an instance entry. See
	* `AppDesiredState.library`. `.optional()` and never `.nullable()`, so an ordinary app entry is
	* byte-identical to the entry before the member.
	*/
	library: AppLibraryDesiredStateSchema.optional(),
	/**
	* 2026-10-13-blob-buckets WS-2 (D13, D14): the blob bucket bindings of this app. See
	* `AppDesiredState.blobBindings` for the two states. The shape is `appBlobBindingsSchema` of
	* `blob-bindings.ts`, the one declaration. `.optional()` and never `.nullable()`, so an entry
	* with no bindings is byte-identical to the entry before the member. No `.transform()`.
	* Absence always means "no bucket": the operator removes the bindings files of a legacy-form
	* fixture and renders a placeholder file for a named-form fixture (R-025). The member has no
	* state for "keep the last file" (rule 4 of `AppDesiredState.blobBindings`).
	* `appBlobBindingsSchema` refuses a member over `APP_BLOB_BINDINGS_MAX_BYTES` of compact JSON
	* (rule 5).
	*
	* The refine below the object refuses a member whose `appSlug` differs from `appId`. The CP
	* producer (WS-4) parses the member value alone with `appBlobBindingsSchema`, because a
	* whole-entry parse refuses a pooled app (the comment on `rightsCertificate`). So the producer
	* must also compare `appSlug` with the entry's `appId` itself.
	*/
	blobBindings: appBlobBindingsSchema.optional(),
	/**
	* 2026-10-13-blob-buckets R-104 (stage 6 contracts): the claim-check bindings of this app. See
	* `AppDesiredState.claimCheckBindings` for the two states and the rules. The shape is
	* `appClaimCheckBindingsSchema` of `claim-check-bindings.ts`, the one declaration.
	* `.optional()` and never `.nullable()`, so an entry with no claim-check bindings is
	* byte-identical to the entry before the member. No `.transform()`.
	*
	* The refine below the object refuses a member whose `appSlug` differs from `appId`. The CP
	* producer (WS-58) parses the member value alone with `appClaimCheckBindingsSchema` (the
	* comment on `rightsCertificate`), so it must also compare `appSlug` with the entry's `appId`
	* itself.
	*/
	claimCheckBindings: appClaimCheckBindingsSchema.optional(),
	/**
	* #4416: the fixture pins of every app version on the data plane. See
	* `AppDesiredState.fixturePinsByVersion`. An empty map is refused, as for
	* `appManifestsByVersion`, so absence is the one carrier of "no map".
	*/
	fixturePinsByVersion: z.record(z.string().min(1), z.array(AppVersionFixturePinSchema).min(1).readonly()).refine((byVersion) => Object.keys(byVersion).length > 0, { message: "fixturePinsByVersion must hold at least one version; omit the member instead" }).optional(),
	/**
	* 2026-10-26-aws-followups D-8 (#4629): the per-app cloud identity of this app. See
	* `AppDesiredState.perAppIdentity` for the two states and `PerAppIdentity` for the rules. The
	* shape is `PerAppIdentitySchema`, the one declaration. `.optional()` and never `.nullable()`,
	* so an entry with no member is byte-identical to the entry before the member. No
	* `.transform()`.
	*
	* The refine below the object checks rule 4 of `PerAppIdentity`:
	*
	* 1. A name whose app part differs from `appId` fails at `perAppIdentity.serviceAccounts.<index>`.
	* 2. A name of `appId` whose fixture part is not a key of `fixtures` fails at the same path.
	* 3. A key of `fixtures` with no name in the list fails at `perAppIdentity.serviceAccounts`.
	*/
	perAppIdentity: PerAppIdentitySchema.optional()
}).strict().superRefine((entry, ctx) => {
	if (entry.perAppIdentity !== void 0) {
		const prefix = `${entry.appId}${PER_APP_ACCOUNT_DELIMITER}`;
		const expected = new Set(Object.keys(entry.fixtures).map((fixtureId) => instanceKey({
			appId: entry.appId,
			fixtureId
		})));
		const listed = new Set(entry.perAppIdentity.serviceAccounts);
		entry.perAppIdentity.serviceAccounts.forEach((name, index) => {
			if (!name.startsWith(prefix)) ctx.addIssue({
				code: "custom",
				path: [
					"perAppIdentity",
					"serviceAccounts",
					index
				],
				message: "perAppIdentity.serviceAccounts must name accounts of appId alone: the identity of one app never rides the entry of another"
			});
			else if (!expected.has(name)) ctx.addIssue({
				code: "custom",
				path: [
					"perAppIdentity",
					"serviceAccounts",
					index
				],
				message: `perAppIdentity.serviceAccounts names ${name}, but fixtures has no such key: each name is the account of one fixture of this entry`
			});
		});
		const missing = [...expected].filter((name) => !listed.has(name)).sort();
		if (missing.length > 0) ctx.addIssue({
			code: "custom",
			path: ["perAppIdentity", "serviceAccounts"],
			message: `perAppIdentity.serviceAccounts must name the account of every fixture; it lacks ${missing.join(", ")}`
		});
	}
	if (entry.blobBindings !== void 0 && entry.blobBindings.appSlug !== entry.appId) ctx.addIssue({
		code: "custom",
		path: ["blobBindings", "appSlug"],
		message: "blobBindings.appSlug must equal appId: the bindings of one app never ride the entry of another"
	});
	if (entry.claimCheckBindings !== void 0 && entry.claimCheckBindings.appSlug !== entry.appId) ctx.addIssue({
		code: "custom",
		path: ["claimCheckBindings", "appSlug"],
		message: "claimCheckBindings.appSlug must equal appId: the bindings of one app never ride the entry of another"
	});
});
/**
* One channel inside an offer. Strict. `key` must equal `privateChannelKeyFor(channelId)`, so a
* public ring name offered as a private key (`beta`) is refused (L-2). A conforming object parses
* to itself. Pure.
*/
const ChannelOfferChannelSchema = z.object({
	channelId: z.uuid(),
	slug: z.string().regex(PRIVATE_CHANNEL_SLUG_PATTERN),
	key: z.string(),
	displayName: channelDisplayNameSchema,
	description: z.string().nullable()
}).strict().superRefine((channel, ctx) => {
	if (!z.uuid().safeParse(channel.channelId).success) return;
	if (channel.key !== privateChannelKeyFor(channel.channelId)) ctx.addIssue({
		code: "custom",
		path: ["key"],
		message: "key is not derived from channelId"
	});
});
/**
* One offer. Strict. Every channel's slug must be covered by `pattern`, and `ownerHandle` must be
* the pattern's handle segment. `channels` holds 1 to `PRIVATE_CHANNELS_PER_NAMESPACE_MAX` entries
* with distinct `channelId`s. `offeredAt` is ISO 8601 UTC. A conforming object parses to itself.
* Pure.
*/
const ChannelOfferSchema = z.object({
	shareId: z.uuid(),
	pattern: z.string().regex(CHANNEL_SHARE_PATTERN),
	ownerHandle: z.string().regex(RESOURCE_SLUG_PATTERN),
	ownerEmail: z.string().min(1),
	offeredAt: z.iso.datetime(),
	channels: z.array(ChannelOfferChannelSchema).min(1).max(PRIVATE_CHANNELS_PER_NAMESPACE_MAX)
}).strict().superRefine((offer, ctx) => {
	if (offer.pattern.split("/")[1] !== offer.ownerHandle) ctx.addIssue({
		code: "custom",
		path: ["ownerHandle"],
		message: "ownerHandle is not the pattern handle"
	});
	const seen = /* @__PURE__ */ new Set();
	offer.channels.forEach((channel, i) => {
		if (!matchesChannelSharePattern(offer.pattern, channel.slug)) ctx.addIssue({
			code: "custom",
			path: [
				"channels",
				i,
				"slug"
			],
			message: "slug is not covered by pattern"
		});
		if (seen.has(channel.channelId)) ctx.addIssue({
			code: "custom",
			path: [
				"channels",
				i,
				"channelId"
			],
			message: "duplicate channelId"
		});
		seen.add(channel.channelId);
	});
});
/**
* `DesiredStateEnvelope.channelOffersByOrg`: CP org id to that org's offers, at most
* `CHANNEL_SHARES_PER_ORG_MAX` offers per org with distinct `shareId`s. `{}` is valid and means
* "revoke every open offer" (R-016). A consumer that validates per entry, keeping siblings, uses
* {@link ChannelOfferSchema} instead of this whole-map schema.
*/
const ChannelOffersByOrgSchema = z.record(z.uuid(), z.array(ChannelOfferSchema).max(CHANNEL_SHARES_PER_ORG_MAX).superRefine((offers, ctx) => {
	const seen = /* @__PURE__ */ new Set();
	offers.forEach((offer, i) => {
		if (seen.has(offer.shareId)) ctx.addIssue({
			code: "custom",
			path: [i, "shareId"],
			message: "duplicate shareId"
		});
		seen.add(offer.shareId);
	});
}));
/**
* The most offers `DesiredStateEnvelope.appOffersByOrg` may carry for one org: 1024. One offer is
* one approved app, so the cap is a bound on the CP app catalog seen by one org, far above the
* catalog of 2026-10-14 (about 34 apps). An over-cap org list is refused, not trimmed. Exported so
* release-engine's producer and this `.max()` read one literal.
*/
const APP_OFFERS_PER_ORG_MAX = 1024;
/** `AppOffer.approvedBy`: one of `APP_OFFER_APPROVALS`. Any other value is a `ZodError`. Pure. */
const AppOfferApprovalSchema = z.enum(APP_OFFER_APPROVALS);
/**
* `AppOffer.sharedBlobBuckets` (2026-10-13-blob-buckets R-096 item 1): 1 to
* `BLOB_BUCKETS_PER_APP_MAX` (16) logical bucket names, each in `blobBucketNameSchema`, strictly
* ascending in byte order. So the list is sorted and distinct, and one set of names has one form
* on the wire. Refusals, each a `ZodError`: `[]` (`too_small` at the list); 17 names (`too_big` at
* the list); a name outside the grammar (at its index); a repeat of the name before it (at the
* index of the repeat, message `duplicate logical bucket name`); a name below the name before it
* (at its index, message `logical bucket names must be sorted in byte order`). Module-private: the
* offer schema is the one carrier. No `.transform()`. Pure.
*/
const AppOfferSharedBlobBucketsSchema = z.array(blobBucketNameSchema).min(1).max(BLOB_BUCKETS_PER_APP_MAX).superRefine((names, ctx) => {
	let previous = null;
	names.forEach((name, i) => {
		if (previous !== null && name === previous) ctx.addIssue({
			code: "custom",
			path: [i],
			message: "duplicate logical bucket name"
		});
		else if (previous !== null && name < previous) ctx.addIssue({
			code: "custom",
			path: [i],
			message: "logical bucket names must be sorted in byte order"
		});
		previous = name;
	});
});
/**
* One offer. Strict. `appId` is a UUID; `appSlug` has the CP app slug grammar, at most 63
* characters and then `RESOURCE_SLUG_PATTERN` (`^[a-z0-9][a-z0-9-]*[a-z0-9]$`), the grammar that
* `createAppSchema` applies to `release.apps.slug`; `approvedBy` is one of `APP_OFFER_APPROVALS`.
* So every slug that the CP create door admits can be offered, a slug with a leading digit
* included (`3d-viewer`). A narrower grammar here makes release-engine refuse the offer, and the
* desired-state filter (D1) then withdraws the app from every data plane (finding
* contracts-WS-1-R2). The envelope's `AppDesiredStateSchema.appId` keeps its older, narrower
* `ID_PART`, and that schema has no runtime consumer. A first-party slug (`isFirstPartyApp`, `default-shell` and `admin-app`)
* is a `ZodError` at `appSlug`, because a first-party app always runs and the operator seeds it
* `accepted`; an `offered` row from the ingest could race that seed. The optional
* `sharedBlobBuckets` (2026-10-13-blob-buckets R-096 item 1) holds 1 to 16 sorted, distinct logical
* bucket names; each refusal of that list is a `ZodError` at `['sharedBlobBuckets', ...]`, as
* `AppOfferSharedBlobBucketsSchema` above names. An absent key stays absent in the output. A
* conforming object parses to itself. No `.transform()`. Pure.
*/
const AppOfferSchema = z.object({
	appId: z.uuid(),
	appSlug: z.string().max(63).regex(RESOURCE_SLUG_PATTERN),
	approvedBy: AppOfferApprovalSchema,
	sharedBlobBuckets: AppOfferSharedBlobBucketsSchema.optional()
}).strict().superRefine((offer, ctx) => {
	if (isFirstPartyApp(offer.appSlug)) ctx.addIssue({
		code: "custom",
		path: ["appSlug"],
		message: "a first-party app is never offered"
	});
});
/**
* `DesiredStateEnvelope.appOffersByOrg`: CP org id (a UUID) to that org's offers, at most
* {@link APP_OFFERS_PER_ORG_MAX} per org, with distinct `appId`s and distinct `appSlug`s inside one
* org. A repeat is a `ZodError` at `[org, index, 'appId']` or `[org, index, 'appSlug']`. `{}` is
* valid and means "the CP offers nothing now". The sort order of the producer is not checked here.
*
* Who calls it: release-engine before it assigns the member, and the operator or core-api at its
* boundary before the ingest. A consumer that keeps the valid siblings of a bad entry validates
* per entry with {@link AppOfferSchema}. A conforming map parses to itself. Pure.
*/
const AppOffersByOrgSchema = z.record(z.uuid(), z.array(AppOfferSchema).max(APP_OFFERS_PER_ORG_MAX).superRefine((offers, ctx) => {
	const ids = /* @__PURE__ */ new Set();
	const slugs = /* @__PURE__ */ new Set();
	offers.forEach((offer, i) => {
		if (ids.has(offer.appId)) ctx.addIssue({
			code: "custom",
			path: [i, "appId"],
			message: "duplicate appId"
		});
		if (slugs.has(offer.appSlug)) ctx.addIssue({
			code: "custom",
			path: [i, "appSlug"],
			message: "duplicate appSlug"
		});
		ids.add(offer.appId);
		slugs.add(offer.appSlug);
	});
}));
/**
* `DesiredStateEnvelope.statusReportAccepts`: what the CP accepts on the status report route.
* Strict: `delta` and `gzip` are both required booleans, and at least one is `true`. `{ delta:
* false, gzip: false }` is a `ZodError` at the root path, because the CP omits the member in that
* case, so one meaning has one encoding. Any other key or a non-boolean is a `ZodError`. A
* conforming object parses to itself.
*
* The operator program parses the whole envelope with `DesiredStateEnvelopeSchema`, which does not
* check this member. The operator program may parse this member at its boundary, the way it parses
* `streams` with `StreamSpecSchema`, and read a member that does not parse as absent.
* Pure.
*/
const StatusReportAcceptsSchema = z.object({
	delta: z.boolean(),
	gzip: z.boolean()
}).strict().refine((accepts) => accepts.delta || accepts.gzip, { message: "statusReportAccepts names at least one accepted form; omit the member otherwise" });
/**
* `DesiredStateEnvelope.statusReportTimestamps` (2026-10-10-status-report-since-timestamps D-4):
* the CP accepts the instant form of the two status report durations. The literal `true` parses to
* itself. `false`, a string, a number, `null` and an object are each a `ZodError` at the root path,
* because the CP omits the member when it does not accept the instant form, so one meaning has one
* encoding.
*
* Who calls it: the operator, at its boundary, the way it parses `statusReportAccepts` with
* {@link StatusReportAcceptsSchema}. A member that does not parse reads as absent, and the operator
* then sends the ages. Pure.
*/
const StatusReportTimestampsSchema = z.literal(true);
/**
* `DesiredStateEnvelope.orgDataLocations`: the allowed list of one data plane and the resolved data
* location of each of its orgs (see {@link OrgDataLocations}).
*
* Valid input:
* - `allowedLocations`: `allowedLocationsSchema` of `@zion/sdk-contracts`, 1 to 16 distinct
*   entries, each in `dataLocationSchema`.
* - `byOrg`: CP org id (a UUID) to one `dataLocationSchema` value, 0 to `PLATFORM_BUCKETS_MAX_ORGS`
*   (10,000) keys. `{}` is valid: no org location resolved.
*
* Refusals, each a `ZodError`:
* - an unknown key at the top level, or a missing member (strict);
* - an `allowedLocations` that fails its schema, at `['allowedLocations', ...]`;
* - an org key that is not a UUID, or a value outside the grammar, at `['byOrg', key]`;
* - more than 10,000 org keys, at `['byOrg']`, message `at most 10000 orgs`;
* - an org location that is not an entry of `allowedLocations`, at `['byOrg', key]`, message
*   `an org data location is an entry of allowedLocations`. The producer gives each org the `ok`
*   answer of `resolveDataLocation` over the same list, so a value outside it is a producer fault.
*
* The sort order of the producer is not checked here, as for `AppOffersByOrgSchema`. Release-engine
* sorts both before it hashes the envelope.
*
* Who calls it: release-engine before it assigns the member, and the operator program at its
* boundary (WS-46). The operator reads a member that does not parse as absent and sends no facts.
* A conforming member parses to itself. No `.transform()`. Pure.
*/
const OrgDataLocationsSchema = z.object({
	allowedLocations: allowedLocationsSchema,
	byOrg: z.record(z.uuid(), dataLocationSchema).refine((byOrg) => Object.keys(byOrg).length <= PLATFORM_BUCKETS_MAX_ORGS, { message: `at most ${PLATFORM_BUCKETS_MAX_ORGS} orgs` })
}).strict().superRefine((member, ctx) => {
	const allowed = new Set(member.allowedLocations);
	for (const [orgId, location] of Object.entries(member.byOrg)) if (!allowed.has(location)) ctx.addIssue({
		code: "custom",
		path: ["byOrg", orgId],
		message: "an org data location is an entry of allowedLocations"
	});
});
/**
* The most rows the desired-state member `blobRelocationStates` and one
* `PUT /internal/v1/blob-relocation-states` body may carry: 256 (`00-index.md` stage 4b contracts
* item 2). One row is at most 437 bytes of compact JSON (a `refused` row with a 64-character
* `appSlug`, a 63-character `logical`, a 31-character `targetLocation` and a 64-character
* `refusal`), so a full member with its commas and brackets is at most 112,129 bytes.
*/
const BLOB_RELOCATION_STATES_MAX = 256;
/** A lower-case UUID: an org id, a request id or a relocation id. */
const relocationIdSchema = z.string().regex(LOWERCASED_UUID_PATTERN, "a lower-case UUID");
/** Whether one state is a state of a plan: every value but `requested` and `refused`. */
function isPlanState(state) {
	return state !== "requested" && state !== "refused";
}
/**
* One row of `DesiredStateEnvelope.blobRelocationStates`: the state of one org admin request or of
* one plan (see {@link BlobRelocationState}). The same row with the DP org id is a row of the
* `PUT /internal/v1/blob-relocation-states` body.
*
* Valid input: each member in its grammar (lower-case UUIDs, the app slug grammar of the bindings
* file, `blobBucketNameSchema`, `dataLocationSchema`, {@link BLOB_RELOCATION_STATE_VALUES},
* {@link RELOCATION_GATES}, {@link BLOB_RELOCATION_REFUSAL_PATTERN}), and the five rules below.
*
* Refusals, each a `ZodError`:
* - an unknown key or a missing member (strict), or a member outside its grammar;
* - at `['relocationId']`: a plan state with a `null` relocation id, or a `requested` or `refused`
*   row with one (message `a plan state names its relocation id; requested and refused name none`);
* - at `['requestId']`: a `requested` or `refused` row with a `null` request id (message
*   `a requested or refused row names its request id`);
* - at `['logical']`: a row with a request id and a `null` logical name, or a row with no request id
*   and a logical name (message `a request row names its logical bucket; a plan row with no request
*   names none`);
* - at `['refusal']`: a `refused` row with a `null` refusal, or another state with a refusal
*   (message `a refused row, and only a refused row, carries a refusal code`);
* - at `['waitingGate']`: a `waiting_approval` row with a `null` gate, or another state with a gate
*   (message `a waiting_approval row, and only that row, names the waiting gate`).
*
* A conforming row parses to itself. No `.transform()`. Pure.
*/
const BlobRelocationStateSchema = z.object({
	orgId: relocationIdSchema,
	requestId: relocationIdSchema.nullable(),
	relocationId: relocationIdSchema.nullable(),
	appSlug: blobBindingsFileSchema.shape.app,
	logical: blobBucketNameSchema.nullable(),
	targetLocation: dataLocationSchema,
	state: z.enum(BLOB_RELOCATION_STATE_VALUES),
	waitingGate: z.enum(RELOCATION_GATES).nullable(),
	refusal: z.string().regex(BLOB_RELOCATION_REFUSAL_PATTERN, "a refusal code").nullable()
}).strict().superRefine((row, ctx) => {
	if (row.relocationId !== null !== isPlanState(row.state)) ctx.addIssue({
		code: "custom",
		path: ["relocationId"],
		message: "a plan state names its relocation id; requested and refused name none"
	});
	if (row.requestId === null && !isPlanState(row.state)) ctx.addIssue({
		code: "custom",
		path: ["requestId"],
		message: "a requested or refused row names its request id"
	});
	if (row.requestId === null !== (row.logical === null)) ctx.addIssue({
		code: "custom",
		path: ["logical"],
		message: "a request row names its logical bucket; a plan row with no request names none"
	});
	if (row.refusal !== null !== (row.state === "refused")) ctx.addIssue({
		code: "custom",
		path: ["refusal"],
		message: "a refused row, and only a refused row, carries a refusal code"
	});
	if (row.waitingGate !== null !== (row.state === "waiting_approval")) ctx.addIssue({
		code: "custom",
		path: ["waitingGate"],
		message: "a waiting_approval row, and only that row, names the waiting gate"
	});
});
/**
* The key of one {@link BlobRelocationState} row: `<orgId>/<appSlug>/<id>`, where `<id>` is the
* request id, or the relocation id of a plan row with no request. A valid row always has one of the
* two. Neither grammar admits `/`, so two distinct rows never give one key.
*
* Who calls it: the row set check of the member and of the PUT body; release-engine, which sorts the
* member by this key before it hashes the envelope (WS-21); the operator program, which compares
* two sets (WS-20). Pure.
*/
function blobRelocationStateKey(row) {
	return `${row.orgId}/${row.appSlug}/${String(row.requestId ?? row.relocationId)}`;
}
/**
* The row set rules of `blobRelocationStates` and of the `PUT /internal/v1/blob-relocation-states`
* body. Each issue sits at the later row of a pair:
*
* - at `[index]`: a row whose {@link blobRelocationStateKey} does not sort after the key of the row
*   before it, in byte order (message `rows in ascending key order, each key once`);
* - at `[index, 'requestId']`: a request id that an earlier row holds (message `duplicate requestId`);
* - at `[index, 'relocationId']`: a relocation id that an earlier row holds (message
*   `duplicate relocationId`);
* - at `[index, 'state']`: a second open row of one `(orgId, appSlug)` pair (message
*   `one open row per (orgId, appSlug)`), see `BLOB_RELOCATION_OPEN_STATES`.
*/
function refineBlobRelocationStateSet(rows, ctx) {
	const requestIds = /* @__PURE__ */ new Set();
	const relocationIds = /* @__PURE__ */ new Set();
	const openPairs = /* @__PURE__ */ new Set();
	let previous;
	rows.forEach((row, i) => {
		const key = blobRelocationStateKey(row);
		if (previous !== void 0 && key <= previous) ctx.addIssue({
			code: "custom",
			path: [i],
			message: "rows in ascending key order, each key once"
		});
		previous = key;
		if (row.requestId !== null) {
			if (requestIds.has(row.requestId)) ctx.addIssue({
				code: "custom",
				path: [i, "requestId"],
				message: "duplicate requestId"
			});
			requestIds.add(row.requestId);
		}
		if (row.relocationId !== null) {
			if (relocationIds.has(row.relocationId)) ctx.addIssue({
				code: "custom",
				path: [i, "relocationId"],
				message: "duplicate relocationId"
			});
			relocationIds.add(row.relocationId);
		}
		if (isOpenBlobRelocationState(row.state)) {
			const pair = `${row.orgId}/${row.appSlug}`;
			if (openPairs.has(pair)) ctx.addIssue({
				code: "custom",
				path: [i, "state"],
				message: "one open row per (orgId, appSlug)"
			});
			openPairs.add(pair);
		}
	});
}
/**
* `DesiredStateEnvelope.blobRelocationStates`: 1 to {@link BLOB_RELOCATION_STATES_MAX} (256)
* {@link BlobRelocationStateSchema} rows in ascending key order, under the rules of
* {@link refineBlobRelocationStateSet}.
*
* Refusals, each a `ZodError`: `[]` at the root (the producer omits the member when it holds no row,
* so one meaning has one encoding); more than 256 rows at the root; a bad row at `[index, ...]`; a
* broken set rule at the paths of {@link refineBlobRelocationStateSet}.
*
* Who calls it: release-engine before it assigns the member (WS-21), and the operator program at its
* boundary (WS-20). The operator reads a member that does not parse as absent this cycle, and puts no
* rows. A conforming member parses to itself. No `.transform()`. Pure.
*/
const BlobRelocationStatesSchema = z.array(BlobRelocationStateSchema).min(1).max(256).superRefine(refineBlobRelocationStateSet);
/** The longest `appKey` of an entry: the `appKey` cap of an app-pool node group, 63 characters, the
*  longest Kubernetes label value. */
const APP_POOL_TIER_CAP_APP_KEY_MAX_LENGTH = 63;
/**
* The order of two `appPoolTierCaps` entries: by `orgId`, then `appKey`, then `pool`, then `tier`,
* each in code-unit order. Negative when `a` sorts first, positive when `b` does, and 0 when the
* four key parts are equal. `maxNodes` is not part of the key.
*
* Who calls it: release-engine sorts the member with it before it hashes the envelope (WS-1d), and
* {@link AppPoolTierCapsSchema} checks the order with it. Pure, never throws.
*/
function compareAppPoolTierCaps(a, b) {
	const left = [
		a.orgId,
		a.appKey,
		a.pool,
		a.tier
	];
	const right = [
		b.orgId,
		b.appKey,
		b.pool,
		b.tier
	];
	for (let i = 0; i < left.length; i += 1) {
		const l = left[i];
		const r = right[i];
		if (l !== r) return l < r ? -1 : 1;
	}
	return 0;
}
/** The bounds of an entry's `maxNodes`: an integer from `APP_POOL_MAX_NODES_MIN` (1) to
*  `APP_POOL_MAX_NODES_MAX` (100), the bounds of a tier's own `maxNodes`. */
const tierCapMaxNodesSchema = z.int().min(APP_POOL_MAX_NODES_MIN).max(APP_POOL_MAX_NODES_MAX);
/**
* One entry of `DesiredStateEnvelope.appPoolTierCaps` (see {@link AppPoolTierCap}).
*
* Valid input:
* - `orgId`: a lower-case UUID, `appPoolOrgIdSchema` of `@zion/sdk-contracts`.
* - `appKey`: `APP_POOL_LABEL_VALUE_PATTERN`, at most 63 characters, the `appKey` grammar of an
*   app-pool node group.
* - `pool`: `appPoolNameSchema`.
* - `tier`: `appPoolTierKeySchema`.
* - `maxNodes`: an integer from `APP_POOL_MAX_NODES_MIN` (1) to `APP_POOL_MAX_NODES_MAX` (100).
*
* A missing or unknown key, or a value outside its grammar, is a `ZodError` at that key. A
* conforming entry parses to itself. No `.transform()`. Pure.
*/
const AppPoolTierCapSchema = z.object({
	orgId: appPoolOrgIdSchema,
	appKey: z.string().max(APP_POOL_TIER_CAP_APP_KEY_MAX_LENGTH).regex(APP_POOL_LABEL_VALUE_PATTERN),
	pool: appPoolNameSchema,
	tier: appPoolTierKeySchema,
	maxNodes: tierCapMaxNodesSchema
}).strict();
/** Whether one tier cap entry is valid on its own in the three members the pool total reads:
*  `pool`, `tier` and `maxNodes`. Pure, never throws. */
function tierCapCountsToTotal(entry) {
	return appPoolNameSchema.safeParse(entry.pool).success && appPoolTierKeySchema.safeParse(entry.tier).success && tierCapMaxNodesSchema.safeParse(entry.maxNodes).success;
}
/** The message of an entry at which the caps of its pool on its cloud first pass the pool ceiling. */
const TIER_CAP_POOL_TOTAL_MESSAGE = `the caps of one pool on one cloud total at most ${APP_POOL_MAX_NODES_MAX}`;
/**
* The pool total of a set of tier cap overrides (2026-10-24-pool-machine-generations D-25, DEF-1).
* On one data plane a pool runs on the tiers of that data plane's cloud, and its node maximum there
* is the sum of their caps. The publish rule `pool-tiers-max-nodes-exceeded` of
* `@zion/sdk-contracts` holds the declared caps of one pool on one cloud at `APP_POOL_MAX_NODES_MAX`
* (100) or less. This check holds the overrides of one pool on one cloud to the same ceiling, so
* two overrides of 60 on two tiers of one pool cannot ask for 120 nodes.
*
* Input: the entries in any order, the function that names the owner of an entry's pool (the org
* and the app: `[orgId, appKey]` in the envelope, `[orgId, appId]` in the route answer), the issue
* path prefix, and the refinement context. The cloud of an entry is the first dot-separated part of
* its `tier` key (`gcp.c4d.spot` is on `gcp`), the `<cloud>.` head that `appPoolTierKey` writes.
*
* Output: one issue per pool and cloud whose caps total more than 100, at `[...prefix, index]` of the
* entry at which the running total first passes 100, message
* `the caps of one pool on one cloud total at most 100`. The caps of one pool on two clouds never
* add up, because no data plane runs both. Raises nothing for a set within the ceiling.
*
* The total counts only the entries that are valid on their own, so one fault raises one issue and
* an invalid entry never hides or invents a total. It skips an entry in these cases:
* - `pool` fails `appPoolNameSchema`, or `tier` fails `appPoolTierKeySchema`;
* - `maxNodes` is not an integer from `APP_POOL_MAX_NODES_MIN` (1) to `APP_POOL_MAX_NODES_MAX`
*   (100). So a cap of -50 beside two caps of 60 does not bring the total of 120 down to 70;
* - its owner, `pool` and `tier` equal those of an entry the total already counted. The repeat
*   carries its own issue, and the tier has one cap, so the total counts it once.
*
* Who calls it: {@link AppPoolTierCapsSchema} and `dpPoolTierCapsResponseSchema` in
* `dp-pool-tier-caps.ts`. The package barrel does not export it. Pure, never throws.
*/
function refineTierCapPoolTotals(entries, ownerOf, prefix, ctx) {
	const totals = /* @__PURE__ */ new Map();
	const counted = /* @__PURE__ */ new Set();
	entries.forEach((entry, index) => {
		if (!tierCapCountsToTotal(entry)) return;
		const owner = ownerOf(entry);
		const tierKey = JSON.stringify([
			...owner,
			entry.pool,
			entry.tier
		]);
		if (counted.has(tierKey)) return;
		counted.add(tierKey);
		const cloud = entry.tier.split(".", 1)[0];
		const key = JSON.stringify([
			...owner,
			entry.pool,
			cloud
		]);
		const before = totals.get(key) ?? 0;
		const after = before + entry.maxNodes;
		totals.set(key, after);
		if (before <= APP_POOL_MAX_NODES_MAX && after > APP_POOL_MAX_NODES_MAX) ctx.addIssue({
			code: "custom",
			path: [...prefix, index],
			message: TIER_CAP_POOL_TOTAL_MESSAGE
		});
	});
}
/** The message of an entry whose key equals the key of the entry before it. */
const TIER_CAP_REPEAT_MESSAGE = "each (orgId, appKey, pool, tier) appears once";
/** The message of an entry whose key sorts before the key of the entry before it. */
const TIER_CAP_ORDER_MESSAGE = "entries ascend by orgId, appKey, pool and tier";
/**
* `DesiredStateEnvelope.appPoolTierCaps`: 1 to `APP_POOL_TIER_CAPS_MAX` (1024) entries of
* {@link AppPoolTierCapSchema}, in ascending {@link compareAppPoolTierCaps} order.
*
* Refusals, each a `ZodError`:
* - `[]` at the root: the producer omits the member when it holds no override, so one meaning has
*   one encoding;
* - more than 1024 entries at the root;
* - a bad entry at `[index, key]`;
* - an entry whose key equals the key of the entry before it, at `[index]`, message
*   `each (orgId, appKey, pool, tier) appears once`;
* - an entry whose key sorts before the key of the entry before it, at `[index]`, message
*   `entries ascend by orgId, appKey, pool and tier`;
* - the entry at which the caps of one `(orgId, appKey, pool)` on one cloud first total more than
*   `APP_POOL_MAX_NODES_MAX` (100), at `[index]`, message
*   `the caps of one pool on one cloud total at most 100` ({@link refineTierCapPoolTotals}, D-25).
*   The cloud is the first part of the tier key.
*
* The entries ascend, so a repeat always sits next to its first, and a check of each neighbour pair
* finds every repeat.
*
* Who calls it: release-engine before it assigns the member (WS-1d), and the operator program at its
* boundary from stage S2 (WS-2b). The operator reads a member that does not parse as absent this
* cycle. A conforming member parses to itself. No `.transform()`. Pure.
*/
const AppPoolTierCapsSchema = z.array(AppPoolTierCapSchema).min(1).max(APP_POOL_TIER_CAPS_MAX).superRefine((entries, ctx) => {
	entries.forEach((entry, index) => {
		const above = entries[index - 1];
		if (above === void 0) return;
		const order = compareAppPoolTierCaps(entry, above);
		if (order === 0) ctx.addIssue({
			code: "custom",
			path: [index],
			message: TIER_CAP_REPEAT_MESSAGE
		});
		if (order < 0) ctx.addIssue({
			code: "custom",
			path: [index],
			message: TIER_CAP_ORDER_MESSAGE
		});
	});
	refineTierCapPoolTotals(entries, (entry) => [entry.orgId, entry.appKey], [], ctx);
});
//#endregion
export { APP_OFFERS_PER_ORG_MAX, AppDeclaredAuditEventOnWireSchema, AppDeclaredPermissionOnWireSchema, AppDesiredStateSchema, AppFixtureSetSchema, AppLibraryDesiredStateSchema, AppOfferApprovalSchema, AppOfferSchema, AppOffersByOrgSchema, AppPlatformPermissionRequestsOnWireSchema, AppPoolTierCapSchema, AppPoolTierCapsSchema, AppVersionFixturePinSchema, BLOB_RELOCATION_STATES_MAX, BlobRelocationStateSchema, BlobRelocationStatesSchema, ChannelOfferChannelSchema, ChannelOfferSchema, ChannelOffersByOrgSchema, CustomerChannelDescriptorSchema, FixtureCapabilitiesOnWireSchema, FixtureConfigValueSchema, FixtureDependencyEntrySchema, FixtureDesiredPlatformSecretSpecSchema, FixtureDesiredResourcesSchema, FixtureIsolationLevelSchema, FixtureMcpCapabilityOnWireSchema, FixtureSecretRefSchema, FixtureVersionedDesiredStateSchema, GcpServiceAccountEmailOnWireSchema, MinimumRingMetadataSchema, OrgDataLocationsSchema, PerAppIdentitySchema, StatusReportAcceptsSchema, StatusReportTimestampsSchema, VersionRouteSchema, WITHHELD_ENTRIES_MAX, WithheldEntrySchema, WithheldListSchema, appComputeOverrideSchema, blobRelocationStateKey, compareAppPoolTierCaps, refineBlobRelocationStateSet, refineTierCapPoolTotals };

//# sourceMappingURL=desired-state.schemas.js.map